import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  CloudUploadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileUnknownOutlined,
  FileWordOutlined,
  LoadingOutlined,
  ReloadOutlined,
  WarningFilled,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Drawer,
  Modal,
  Popconfirm,
  Progress,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import { transportApi } from "../../api/transportApi";
import { useAuthStore } from "../../stores/authStore";
import { useFyStore } from "../../stores/fyStore";
import type { AttachmentDto } from "../../types/api";

const DEFAULT_ORG_ID = Number(import.meta.env.VITE_ORG_ID ?? 1);
const { Text } = Typography;

// ─── Upload constraints ───────────────────────────────────────────────────────
const ACCEPTED_EXTS = new Set(["jpg", "jpeg", "png", "webp", "pdf"]);
const ACCEPTED_ACCEPT = ".jpg,.jpeg,.png,.webp,.pdf";
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

type UploadStatus = "pending" | "uploading" | "done" | "error";

type QueueItem = {
  uid: string;
  file: File;
  docType: string;
  /** Local blob URL — only set for image files */
  previewUrl: string | null;
  status: UploadStatus;
  progress: number;
  errorMsg?: string;
};

// ─── Document type options per entity ────────────────────────────────────────
const DOC_TYPES_BY_ENTITY: Record<string, { value: string; label: string }[]> = {
  consignment: [
    { value: "LR", label: "LR Copy" },
    { value: "Invoice", label: "Invoice" },
    { value: "EWayBill", label: "E-Way Bill" },
    { value: "POD", label: "Proof of Delivery" },
    { value: "WeighBridge", label: "Weigh Bridge" },
    { value: "Other", label: "Other" },
  ],
  manifest: [
    { value: "ManifestCopy", label: "Manifest Copy" },
    { value: "LoadingSlip", label: "Loading Slip" },
    { value: "GatePass", label: "Gate Pass" },
    { value: "FreightBill", label: "Freight Bill" },
    { value: "Receipt", label: "Receipt" },
    { value: "Other", label: "Other" },
  ],
  vehicle: [
    { value: "RC", label: "Registration Certificate" },
    { value: "Insurance", label: "Insurance Policy" },
    { value: "FitnessC", label: "Fitness Certificate" },
    { value: "Permit", label: "Road Permit" },
    { value: "PollutionC", label: "Pollution Certificate" },
    { value: "Other", label: "Other" },
  ],
  driver: [
    { value: "License", label: "Driving License" },
    { value: "Aadhar", label: "Aadhar Card" },
    { value: "PAN", label: "PAN Card" },
    { value: "Photo", label: "Passport Photo" },
    { value: "MedicalC", label: "Medical Certificate" },
    { value: "Other", label: "Other" },
  ],
  lorry_hire: [
    { value: "Challan", label: "Hire Challan" },
    { value: "FreightBill", label: "Freight Bill" },
    { value: "Receipt", label: "Payment Receipt" },
    { value: "Agreement", label: "Agreement" },
    { value: "Other", label: "Other" },
  ],
};

const DEFAULT_DOC_TYPES = [
  { value: "Invoice", label: "Invoice" },
  { value: "Receipt", label: "Receipt" },
  { value: "Other", label: "Other" },
];

function getDocTypes(entityType: string) {
  return DOC_TYPES_BY_ENTITY[entityType.toLowerCase()] ?? DEFAULT_DOC_TYPES;
}

// ─── File icon ────────────────────────────────────────────────────────────────
function FileIcon({ ext }: { ext: string }) {
  const e = (ext ?? "").toLowerCase().replace(".", "");
  if (e === "pdf") return <FilePdfOutlined style={{ color: "#ff4d4f", fontSize: 22 }} />;
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(e))
    return <FileImageOutlined style={{ color: "#52c41a", fontSize: 22 }} />;
  if (["xls", "xlsx", "csv"].includes(e))
    return <FileExcelOutlined style={{ color: "#389e0d", fontSize: 22 }} />;
  if (["doc", "docx"].includes(e))
    return <FileWordOutlined style={{ color: "#1677ff", fontSize: 22 }} />;
  if (["txt", "log"].includes(e))
    return <FileTextOutlined style={{ color: "#722ed1", fontSize: 22 }} />;
  return <FileUnknownOutlined style={{ color: "#888", fontSize: 22 }} />;
}

function formatBytes(bytes: number) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Auth-gated blob fetch ────────────────────────────────────────────────────
interface FetchHeaders {
  token: string | null;
  orgId: number;
  fyId: number;
  fyCode: string | null;
}

async function fetchAuthBlobUrl(relativeUrl: string, h: FetchHeaders): Promise<string> {
  const headers: Record<string, string> = { "X-Org-Id": String(h.orgId) };
  if (h.token) headers["Authorization"] = `Bearer ${h.token}`;
  if (h.fyId) headers["X-FY-ID"] = String(h.fyId);
  if (h.fyCode) headers["X-FY"] = h.fyCode;
  const resp = await fetch(relativeUrl, { headers });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return URL.createObjectURL(await resp.blob());
}

const AttachmentThumbnail: React.FC<{ attachment: AttachmentDto }> = ({ attachment }) => {
  const token = useAuthStore((s) => s.accessToken);
  const fyId = useFyStore((s) => s.activeFyId);
  const fyCode = useFyStore((s) => s.activeFyCode);
  const [url, setUrl] = useState<string | null>(null);
  const ext = (attachment.fileExtension ?? "").toLowerCase().replace(".", "");
  const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);

  useEffect(() => {
    if (!isImage) return;
    let active = true;
    let objectUrl: string | null = null;
    fetchAuthBlobUrl(attachment.downloadUrl, { token, orgId: DEFAULT_ORG_ID, fyId, fyCode })
      .then((nextUrl) => {
        objectUrl = nextUrl;
        if (active) setUrl(nextUrl);
        else URL.revokeObjectURL(nextUrl);
      })
      .catch(() => undefined);
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.id, attachment.downloadUrl, token, fyId, fyCode, isImage]);

  return (
    <div style={{ width: 48, height: 48, borderRadius: 6, overflow: "hidden", border: "1px solid #e8e8e8", background: "#fafafa", display: "flex", alignItems: "center", justifyContent: "center" }}>
      {isImage && url
        ? <img src={url} alt={attachment.fileName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        : <FileIcon ext={ext} />}
    </div>
  );
};

// ─── Props ────────────────────────────────────────────────────────────────────
interface EntityAttachmentsPanelProps {
  entityType: string;
  /** Permanent entity id after save. Use 0 / null before save and supply tempKey instead. */
  entityId: number | undefined | null;
  /** Pre-save temp key — attach files before entity exists. Pass to uploadAttachment. */
  tempKey?: string | null;
  entityLabel?: string;
  /**
   * Admin-supplied branch for temp-mode uploads.
   * Regular users are locked to their JWT branch server-side and do not need this.
   * Admins must supply the target branch so the uploaded file is stamped correctly.
   */
  branchId?: number;
}

// ─── Preview modal ────────────────────────────────────────────────────────────
interface PreviewModalProps {
  target: AttachmentDto | null;
  onClose: () => void;
}

const PreviewModal: React.FC<PreviewModalProps> = ({ target, onClose }) => {
  const token = useAuthStore((s) => s.accessToken);
  const fyId = useFyStore((s) => s.activeFyId);
  const fyCode = useFyStore((s) => s.activeFyCode);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loadingBlob, setLoadingBlob] = useState(false);
  const prevBlobRef = useRef<string | null>(null);

  useEffect(() => {
    if (prevBlobRef.current) { URL.revokeObjectURL(prevBlobRef.current); prevBlobRef.current = null; }
    setBlobUrl(null);
    if (!target) return;
    setLoadingBlob(true);
    fetchAuthBlobUrl(target.downloadUrl, { token, orgId: DEFAULT_ORG_ID, fyId, fyCode })
      .then((url) => { setBlobUrl(url); prevBlobRef.current = url; })
      .catch(() => message.error("Failed to load preview"))
      .finally(() => setLoadingBlob(false));
  }, [target?.id]);

  useEffect(() => () => { if (prevBlobRef.current) URL.revokeObjectURL(prevBlobRef.current); }, []);

  const triggerDownload = () => {
    if (!blobUrl || !target) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = target.fileName;
    a.click();
  };

  const ext = (target?.fileExtension ?? "").toLowerCase().replace(".", "");
  const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
  const isPdf = ext === "pdf";

  return (
    <Modal
      title={target?.fileName}
      open={!!target}
      onCancel={onClose}
      footer={
        <Button icon={<DownloadOutlined />} onClick={triggerDownload} disabled={!blobUrl}>
          Download
        </Button>
      }
      width={800}
      destroyOnHidden
    >
      {loadingBlob ? (
        <div style={{ textAlign: "center", padding: "48px 0" }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} />} />
          <div style={{ marginTop: 12, color: "#888" }}>Loading preview…</div>
        </div>
      ) : blobUrl ? (
        isImage ? (
          <img src={blobUrl} alt={target?.fileName} style={{ width: "100%", maxHeight: 520, objectFit: "contain" }} />
        ) : isPdf ? (
          <iframe src={blobUrl} title={target?.fileName} style={{ width: "100%", height: 520, border: "none" }} />
        ) : (
          <div style={{ textAlign: "center", padding: "32px 0" }}>
            <FileUnknownOutlined style={{ fontSize: 48, color: "#aaa", marginBottom: 12 }} />
            <div>Preview not available for this file type.</div>
            <Button type="primary" icon={<DownloadOutlined />} style={{ marginTop: 12 }} onClick={triggerDownload}>
              Download to view
            </Button>
          </div>
        )
      ) : null}
    </Modal>
  );
};

// ─── Drop zone ────────────────────────────────────────────────────────────────
interface DropZoneProps {
  onFiles: (files: File[]) => void;
}

const DropZone: React.FC<DropZoneProps> = ({ onFiles }) => {
  const [isDragging, setIsDragging] = useState(false);
  const dragCountRef = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCountRef.current++;
    setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCountRef.current--;
    if (dragCountRef.current <= 0) { dragCountRef.current = 0; setIsDragging(false); }
  };
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCountRef.current = 0;
    setIsDragging(false);
    if (e.dataTransfer.files.length) onFiles(Array.from(e.dataTransfer.files));
  };
  const handleClick = () => inputRef.current?.click();
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) onFiles(Array.from(e.target.files));
    e.target.value = "";
  };

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && handleClick()}
      style={{
        border: `2px dashed ${isDragging ? "#1677ff" : "#d9d9d9"}`,
        borderRadius: 10,
        background: isDragging ? "#e6f4ff" : "#fafafa",
        padding: "10px 16px 8px",
        textAlign: "center",
        cursor: "pointer",
        transition: "border-color 0.2s, background 0.2s",
        userSelect: "none",
        outline: "none",
      }}
    >
      <CloudUploadOutlined
        style={{
          fontSize: 26,
          color: isDragging ? "#1677ff" : "#c0c0c0",
          display: "block",
          marginBottom: 4,
          transition: "color 0.2s",
        }}
      />
      <div style={{ fontSize: 14, fontWeight: 600, color: isDragging ? "#1677ff" : "#444", marginBottom: 4 }}>
        {isDragging ? "Drop files here…" : "Click to upload or drag & drop"}
      </div>
      <div style={{ fontSize: 12, color: "#aaa" }}>
        JPG&nbsp;·&nbsp;PNG&nbsp;·&nbsp;WEBP&nbsp;·&nbsp;PDF
        &nbsp;&nbsp;|&nbsp;&nbsp;Max&nbsp;10&nbsp;MB per file
      </div>
      <input ref={inputRef} type="file" multiple accept={ACCEPTED_ACCEPT} style={{ display: "none" }} onChange={handleInputChange} />
    </div>
  );
};

// ─── Upload queue card ────────────────────────────────────────────────────────
interface UploadCardProps {
  item: QueueItem;
  docTypes: { value: string; label: string }[];
  onDocTypeChange: (uid: string, type: string) => void;
  onRemove: (uid: string) => void;
  onRetry: (uid: string) => void;
}

const UploadCard: React.FC<UploadCardProps> = ({ item, docTypes, onDocTypeChange, onRemove, onRetry }) => {
  const ext = item.file.name.split(".").pop()?.toLowerCase() ?? "";
  const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);

  const cardBg =
    item.status === "done" ? "#f6ffed" :
    item.status === "error" ? "#fff2f0" :
    item.status === "uploading" ? "#f0f5ff" :
    "#fafafa";

  const cardBorder =
    item.status === "done" ? "#b7eb8f" :
    item.status === "error" ? "#ffccc7" :
    item.status === "uploading" ? "#adc6ff" :
    "#f0f0f0";

  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        padding: "10px 12px 8px",
        borderRadius: 8,
        border: `1px solid ${cardBorder}`,
        background: cardBg,
        alignItems: "flex-start",
        transition: "all 0.2s",
      }}
    >
      {/* Thumbnail / icon */}
      <div
        style={{
          width: 52,
          height: 52,
          flexShrink: 0,
          borderRadius: 7,
          overflow: "hidden",
          background: "#fff",
          border: "1px solid #e8e8e8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isImage && item.previewUrl ? (
          <img src={item.previewUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <FileIcon ext={ext} />
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Header row: name + action */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <Text
              ellipsis={{ tooltip: item.file.name }}
              style={{ fontSize: 13, fontWeight: 600, display: "block", lineHeight: "1.3" }}
            >
              {item.file.name}
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {formatBytes(item.file.size)}
              {item.status === "uploading" && item.progress < 100 && (
                <span style={{ marginLeft: 6, color: "#1677ff" }}>{item.progress}%</span>
              )}
            </Text>
          </div>

          {/* Right-side action / icon */}
          <div style={{ flexShrink: 0, paddingTop: 2 }}>
            {item.status === "pending" && (
              <Tooltip placement="bottom" title="Remove">
                <Button
                  size="small"
                  type="text"
                  icon={<CloseCircleFilled style={{ color: "#bbb", fontSize: 16 }} />}
                  onClick={() => onRemove(item.uid)}
                  style={{ padding: 0, height: "auto" }}
                />
              </Tooltip>
            )}
            {item.status === "uploading" && (
              <LoadingOutlined style={{ color: "#1677ff", fontSize: 18 }} />
            )}
            {item.status === "done" && (
              <CheckCircleFilled style={{ color: "#52c41a", fontSize: 20 }} />
            )}
            {item.status === "error" && (
              <WarningFilled style={{ color: "#ff4d4f", fontSize: 18 }} />
            )}
          </div>
        </div>

        {/* Progress bar */}
        {item.status === "uploading" && (
          <Progress
            percent={item.progress}
            size="small"
            showInfo={false}
            strokeColor={{ "0%": "#1677ff", "100%": "#52c41a" }}
            style={{ margin: "6px 0 4px" }}
          />
        )}

        {/* Done message */}
        {item.status === "done" && (
          <Text style={{ fontSize: 11, color: "#52c41a", display: "block", marginTop: 3 }}>
            Uploaded successfully
          </Text>
        )}

        {/* Error message + retry */}
        {item.status === "error" && (
          <div style={{ marginTop: 3 }}>
            <Text style={{ fontSize: 11, color: "#ff4d4f", display: "block" }}>
              {item.errorMsg ?? "Upload failed"}
            </Text>
            <Button
              size="small"
              type="link"
              danger
              style={{ padding: 0, height: "auto", fontSize: 12 }}
              onClick={() => onRetry(item.uid)}
            >
              Retry
            </Button>
          </div>
        )}

        {/* Document type (only when editable) */}
        {(item.status === "pending" || item.status === "error") && (
          <Select
            size="small"
            style={{ width: "100%", marginTop: 7 }}
            value={item.docType}
            options={docTypes}
            onChange={(v) => onDocTypeChange(item.uid, v)}
            popupMatchSelectWidth={false}
          />
        )}
        {item.status === "done" && (
          <Tag style={{ marginTop: 5, fontSize: 11 }}>{docTypes.find((d) => d.value === item.docType)?.label ?? item.docType}</Tag>
        )}
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
export const EntityAttachmentsPanel: React.FC<EntityAttachmentsPanelProps> = ({
  entityType,
  entityId,
  tempKey,
  entityLabel,
  branchId,
}) => {
  const token = useAuthStore((s) => s.accessToken);
  const fyId = useFyStore((s) => s.activeFyId);
  const fyCode = useFyStore((s) => s.activeFyCode);

  const [rows, setRows] = useState<AttachmentDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [previewTarget, setPreviewTarget] = useState<AttachmentDto | null>(null);
  const [downloading, setDownloading] = useState<Record<string, boolean>>({});

  const docTypes = getDocTypes(entityType);

  const isTempMode = !!tempKey && (!entityId || entityId === 0);

  // ── Load existing attachments ──────────────────────────────────────────────
  const load = useCallback(async () => {
    if (isTempMode && tempKey) {
      setLoading(true);
      try { setRows(await transportApi.getAttachmentsByTempKey(tempKey)); }
      catch { message.error("Failed to load documents"); }
      finally { setLoading(false); }
    } else if (entityId) {
      setLoading(true);
      // Use entity-scoped endpoint so the server enforces entity authorization
      // rather than letting the generic list endpoint bypass it.
      try { setRows(await transportApi.getAttachmentsByEntity("transport", entityType, entityId)); }
      catch { message.error("Failed to load documents"); }
      finally { setLoading(false); }
    }
  }, [entityType, entityId, tempKey, isTempMode]);

  useEffect(() => { void load(); }, [load]);

  // ── Drawer open / close ────────────────────────────────────────────────────
  const openUpload = () => { setQueue([]); setUploadOpen(true); };

  const closeUploadDrawer = () => {
    // revoke local preview blob URLs
    queue.forEach((q) => { if (q.previewUrl) URL.revokeObjectURL(q.previewUrl); });
    setQueue([]);
    setUploadOpen(false);
  };

  // ── Add files to queue ─────────────────────────────────────────────────────
  const addFiles = (rawFiles: File[]) => {
    const newItems: QueueItem[] = [];
    for (const file of rawFiles) {
      const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
      if (!ACCEPTED_EXTS.has(ext)) {
        message.warning({ content: `"${file.name}" — unsupported type. Allowed: JPG, PNG, WEBP, PDF.`, key: file.name });
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        message.warning({ content: `"${file.name}" exceeds the 10 MB file size limit.`, key: file.name });
        continue;
      }
      const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
      newItems.push({
        uid: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        docType: docTypes[0].value,
        previewUrl: isImage ? URL.createObjectURL(file) : null,
        status: "pending",
        progress: 0,
      });
    }
    if (newItems.length) {
      setQueue((prev) => [...prev, ...newItems]);
      newItems.forEach((item) => void uploadOne(item));
    }
  };

  const changeDocType = (uid: string, docType: string) => {
    setQueue((prev) => prev.map((q) => (q.uid === uid ? { ...q, docType } : q)));
  };

  const removeItem = (uid: string) => {
    setQueue((prev) => {
      const item = prev.find((q) => q.uid === uid);
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((q) => q.uid !== uid);
    });
  };

  // ── Upload a single item ───────────────────────────────────────────────────
  async function uploadOne(item: QueueItem) {
    if (!entityId && !tempKey) return;

    // In temp mode, an admin must have selected a branch before uploading.
    // Regular users are server-locked to their JWT branch — no client branchId needed.
    if (isTempMode && branchId === undefined) {
      setQueue((prev) =>
        prev.map((q) =>
          q.uid === item.uid
            ? { ...q, status: "error", errorMsg: "Select a branch before uploading documents." }
            : q
        )
      );
      return;
    }

    // mark uploading
    setQueue((prev) =>
      prev.map((q) => (q.uid === item.uid ? { ...q, status: "uploading", progress: 0, errorMsg: undefined } : q))
    );
    try {
      await transportApi.uploadAttachment({
        module: "Transport",
        entityType,
        entityId: isTempMode ? 0 : (entityId ?? 0),
        documentType: item.docType,
        tempKey: isTempMode ? tempKey : undefined,
        // Pass branchId only in temp mode — permanent-entity uploads are branch-locked server-side
        branchId: isTempMode ? branchId : undefined,
        file: item.file,
        onUploadProgress: (percent) => {
          setQueue((prev) =>
            prev.map((q) => (q.uid === item.uid ? { ...q, progress: percent } : q))
          );
        },
      });
      // Bump to 100 and mark done
      setQueue((prev) =>
        prev.map((q) => (q.uid === item.uid ? { ...q, status: "done", progress: 100 } : q))
      );
      await load(); // move the completed upload into the main attachment list
      removeItem(item.uid);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        "Upload failed. Please try again.";
      setQueue((prev) =>
        prev.map((q) => (q.uid === item.uid ? { ...q, status: "error", errorMsg: msg } : q))
      );
    }
  }

  // ── Upload all pending / errored items ─────────────────────────────────────
  const uploadAll = () => {
    queue
      .filter((q) => q.status === "pending" || q.status === "error")
      .forEach((q) => void uploadOne(q));
  };

  const retryItem = (uid: string) => {
    const item = queue.find((q) => q.uid === uid);
    if (item) void uploadOne(item);
  };

  // ── Download ───────────────────────────────────────────────────────────────
  const onDownload = async (r: AttachmentDto) => {
    setDownloading((prev) => ({ ...prev, [r.id]: true }));
    try {
      const blobUrl = await fetchAuthBlobUrl(r.downloadUrl, { token, orgId: DEFAULT_ORG_ID, fyId, fyCode });
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = r.fileName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
    } catch {
      message.error("Download failed");
    } finally {
      setDownloading((prev) => ({ ...prev, [r.id]: false }));
    }
  };

  const onDelete = async (id: string) => {
    try {
      await transportApi.deleteAttachment(id);
      message.success("Document deleted");
      load();
    } catch {
      message.error("Failed to delete document");
    }
  };

  // ── Derived upload state ───────────────────────────────────────────────────
  const uploadableCount = queue.filter((q) => q.status === "pending" || q.status === "error").length;
  const uploadingCount = queue.filter((q) => q.status === "uploading").length;
  const allDone = queue.length > 0 && queue.every((q) => q.status === "done");

  // ── Guard: need either entityId or tempKey ─────────────────────────────────
  if (!entityId && !tempKey) {
    return (
      <Alert type="info" showIcon message="Save the record first to manage documents." style={{ margin: "8px 0" }} />
    );
  }

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {rows.length} document{rows.length !== 1 ? "s" : ""} attached
        </Text>
        <Space size={6}>
          <Tooltip placement="bottom" title="Refresh">
            <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading} />
          </Tooltip>
        </Space>
      </div>

      {/* Existing documents */}
      <Spin spinning={loading}>
        {rows.length === 0 ? (
          <div
            style={{
              border: "1px dashed #d9d9d9",
              borderRadius: 8,
              padding: "24px 0",
              textAlign: "center",
              cursor: "default",
              color: "#aaa",
              transition: "all 0.15s",
            }}
          >
            <CloudUploadOutlined style={{ fontSize: 28, marginBottom: 6 }} />
            <div style={{ fontSize: 13 }}>No documents yet. Add files below.</div>
          </div>
        ) : (
          <Table
            className="entry-inline-table"
            style={{ display: "block" }}
            dataSource={rows}
            rowKey="id"
            size="small"
            pagination={rows.length > 10 ? { pageSize: 10, size: "small" } : false}
            columns={[
              {
                title: "",
                width: 64,
                render: (_: unknown, r: AttachmentDto) => <AttachmentThumbnail attachment={r} />,
              },
              {
                title: "File",
                dataIndex: "fileName",
                ellipsis: true,
                render: (name: string, r: AttachmentDto) => (
                  <Tooltip placement="bottom" title={name}>
                    <Text style={{ fontSize: 12, cursor: "pointer" }} onClick={() => setPreviewTarget(r)}>
                      {name}
                    </Text>
                  </Tooltip>
                ),
              },
              {
                title: "Type",
                dataIndex: "documentType",
                width: 110,
                render: (t: string) => <Tag style={{ fontSize: 11, margin: 0 }}>{t}</Tag>,
              },
              {
                title: "Size",
                dataIndex: "fileSize",
                width: 70,
                render: (s: number) => <Text type="secondary" style={{ fontSize: 11 }}>{formatBytes(s)}</Text>,
              },
              {
                title: "Date",
                dataIndex: "uploadedAt",
                width: 80,
                render: (d: string) => (
                  <Text type="secondary" style={{ fontSize: 11 }}>{dayjs(d).format("DD MMM YY")}</Text>
                ),
              },
              {
                title: "",
                width: 72,
                render: (_: unknown, r: AttachmentDto) => (
                  <Space size={2}>
                    <Tooltip placement="bottom" title="Preview">
                      <Button size="small" type="text" icon={<EyeOutlined />} onClick={() => setPreviewTarget(r)} />
                    </Tooltip>
                    <Tooltip placement="bottom" title="Download">
                      <Button
                        size="small"
                        type="text"
                        icon={<DownloadOutlined />}
                        loading={downloading[r.id]}
                        onClick={() => void onDownload(r)}
                      />
                    </Tooltip>
                    <Popconfirm
                      title="Delete document?"
                      onConfirm={() => void onDelete(r.id)}
                      okText="Delete"
                      okButtonProps={{ danger: true }}
                    >
                      <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
          />
        )}
      </Spin>

      {/* Inline uploader shared by every entry page using this panel. */}
      <div style={{ marginTop: 10, padding: 8, border: "1px dashed #d9d9d9", borderRadius: 10, background: "#fafafa" }}>
        {queue.length > 0 && (
          <>
            <Text type="secondary" style={{ display: "block", fontSize: 12, margin: "2px 4px 7px" }}>{queue.length} file{queue.length !== 1 ? "s" : ""} uploading</Text>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 8 }}>
              {queue.map((item) => <UploadCard key={item.uid} item={item} docTypes={docTypes} onDocTypeChange={changeDocType} onRemove={removeItem} onRetry={retryItem} />)}
            </div>
          </>
        )}
        <div style={{ marginTop: queue.length > 0 ? 8 : 0 }}><DropZone onFiles={addFiles} /></div>
      </div>

      {/* ── Upload drawer ── */}
      <Drawer
        title={
          <Space>
            <CloudUploadOutlined style={{ color: "#1677ff" }} />
            Upload Documents
            {entityLabel && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                — {entityLabel}
              </Text>
            )}
          </Space>
        }
        open={uploadOpen}
        onClose={closeUploadDrawer}
        width={520}
        maskClosable={queue.every((q) => q.status === "done" || q.status === "uploading") || queue.length === 0}
        destroyOnClose
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={closeUploadDrawer}>{allDone ? "Close" : "Cancel"}</Button>
            {!allDone && (
              <Button
                type="primary"
                icon={<CloudUploadOutlined />}
                disabled={uploadableCount === 0}
                loading={uploadingCount > 0 && uploadableCount === 0}
                onClick={uploadAll}
              >
                {uploadingCount > 0
                  ? `Uploading… (${queue.filter((q) => q.status === "done").length}/${queue.length})`
                  : uploadableCount > 0
                  ? `Upload ${uploadableCount} file${uploadableCount !== 1 ? "s" : ""}`
                  : "Upload"}
              </Button>
            )}
          </div>
        }
      >
        {/* Drop zone */}
        <DropZone onFiles={addFiles} />

        {/* Queue */}
        {queue.length > 0 && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                margin: "16px 0 8px",
              }}
            >
              <Text type="secondary" style={{ fontSize: 12 }}>
                {queue.length} file{queue.length !== 1 ? "s" : ""} queued
              </Text>
              {allDone && (
                <Text style={{ fontSize: 12, color: "#52c41a", fontWeight: 600 }}>
                  <CheckCircleFilled style={{ marginRight: 4 }} />
                  All files uploaded!
                </Text>
              )}
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                maxHeight: 400,
                overflowY: "auto",
                paddingRight: 2,
              }}
            >
              {queue.map((item) => (
                <UploadCard
                  key={item.uid}
                  item={item}
                  docTypes={docTypes}
                  onDocTypeChange={changeDocType}
                  onRemove={removeItem}
                  onRetry={retryItem}
                />
              ))}
            </div>
          </>
        )}

        {/* Empty state hint */}
        {queue.length === 0 && (
          <div style={{ marginTop: 16, textAlign: "center", color: "#bbb", fontSize: 12 }}>
            Select or drop files above to begin
          </div>
        )}
      </Drawer>

      {/* Preview modal */}
      <PreviewModal target={previewTarget} onClose={() => setPreviewTarget(null)} />
    </div>
  );
};
